import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Download a file from a URL (handles both absolute and relative paths).
 */
export async function downloadFile(url, filename = 'download.png') {
  try {
    const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
    const response = await fetch(fullUrl);
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
    toast.success('Downloaded!');
    return true;
  } catch (err) {
    console.error('Download failed:', err);
    const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
    window.open(fullUrl, '_blank');
    toast.info('Opened in new tab — right-click to save');
    return false;
  }
}

/**
 * Share content using native device share sheet.
 * On mobile: shows the device share sheet (AirDrop, Messages, social apps, etc.)
 * On desktop without share API: copies link to clipboard.
 */
export async function shareContent(title, text, url) {
  const fullUrl = url?.startsWith('http') ? url : url ? `${API}${url}` : window.location.href;

  // Try native share (works on mobile browsers)
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url: fullUrl });
      return true;
    } catch (err) {
      if (err.name === 'AbortError') return false;
    }
  }

  // Fallback: copy link
  try {
    await navigator.clipboard.writeText(fullUrl);
    toast.success('Link copied to clipboard!');
  } catch {
    const input = document.createElement('input');
    input.value = fullUrl;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    toast.success('Link copied!');
  }
  return true;
}

/**
 * Share a content file via native device share sheet.
 * On mobile: opens share sheet with the actual file attached
 * On desktop: copies the download URL to clipboard (no forced download)
 */
export async function shareOrDownload(fileUrl, contentType = 'image/png', title = 'Content') {
  if (!fileUrl) {
    toast.error('No content to share');
    return;
  }

  const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API}${fileUrl}`;

  // Try native share with file (mobile devices)
  if (navigator.share) {
    try {
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const ext = contentType.includes('png') ? 'png' : contentType.includes('mp4') ? 'mp4' : contentType.includes('mp3') ? 'mp3' : 'png';
      const file = new File([blob], `${title}.${ext}`, { type: contentType });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title, files: [file] });
        return;
      }
      // If can't share files, share the URL
      await navigator.share({ title, text: `${title} — Made with ScalitOS`, url: fullUrl });
      return;
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Fall through to clipboard
    }
  }

  // Desktop fallback: copy link to clipboard (NOT download)
  try {
    await navigator.clipboard.writeText(fullUrl);
    toast.success('Link copied to clipboard! Paste anywhere to share.');
  } catch {
    // Last resort fallback
    const input = document.createElement('input');
    input.value = fullUrl;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    toast.success('Link copied!');
  }
}
